import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for the triangle and draggable ball
const TriangleContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '320px',
    height: '300px',
    margin: '20px auto',
    backgroundColor: 'navy.main',
    border: 'slate_gray.main',
    borderRadius: '4px',
}));

const Triangle = styled('div')({
    position: 'absolute',
    top: '40px',
    left: '30px',
    width: '260px',
    height: '225px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const TriangleFill = styled('div')({
    position: 'absolute',
    width: '100%',
    height: '100%',
    clipPath: 'polygon(50% 100%, 0% 0%, 100% 0%)',
    backgroundColor: 'rgba(4, 7, 9, 0.42)',
});

const DraggableBall = styled(Box)(({ theme, position }) => ({
    position: 'absolute',
    width: '20px',
    height: '20px',
    backgroundColor: '#c2a66e',
    borderRadius: '50%',
    cursor: 'grab',
    transform: `translate(${position.x - 10}px, ${position.y - 10}px)`,
    '&:active': {
        cursor: 'grabbing',
    },
    zIndex: 1,
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
}));

const Label = styled(Typography)(({ theme }) => ({
    position: 'absolute',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1976d2',
    zIndex: 1,
}));

function WeightingDialog({ open, onClose }) {
    const [weights, setWeights] = useState({
        distance: 0.33,
        speed: 0.33,
        payload: 0.34,
    });
    
    // Triangle dimensions and vertices (in triangle-local coordinates)
    const triangleLeft = 30;
    const triangleTop = 40;
    const triangleWidth = 260;
    const triangleHeight = 225;
    
    // Triangle vertices in local coordinates
    const vertices = {
        payload: { x: triangleWidth/2, y: triangleHeight }, // bottom (payload)
        distance: { x: 0, y: 0 }, // top left (distance)
        speed: { x: triangleWidth, y: 0 }, // top right (speed)
    };

    // Calculate barycentric coordinates
    const calculateBarycentricCoordinates = (px, py) => {
        // Convert to local coordinates
        const x = px - triangleLeft;
        const y = py - triangleTop;

        // Triangle vertices
        const p1 = vertices.payload;  // bottom
        const p2 = vertices.distance; // top left
        const p3 = vertices.speed;    // top right

        // Calculate area of the full triangle
        const fullArea = ((p2.x - p1.x) * (p3.y - p1.y) - (p3.x - p1.x) * (p2.y - p1.y)) / 2;

        // Calculate barycentric coordinates using subtriangle areas
        const alpha = ((p2.x - x) * (p3.y - y) - (p3.x - x) * (p2.y - y)) / (2 * fullArea);
        const beta = ((p3.x - x) * (p1.y - y) - (p1.x - x) * (p3.y - y)) / (2 * fullArea);
        const gamma = 1 - alpha - beta;

        // Add small epsilon to prevent exact 0 values
        const epsilon = 0.0001;
        
        // Clamp values between epsilon and 1
        const clamp = (value) => Math.max(epsilon, Math.min(1, value));
        
        // Get raw coordinates
        const coords = {
            payload: clamp(alpha),
            distance: clamp(beta),
            speed: clamp(gamma)
        };

        // Normalize to ensure they sum to 1
        const sum = coords.payload + coords.distance + coords.speed;
        return {
            payload: coords.payload / sum,
            distance: coords.distance / sum,
            speed: coords.speed / sum
        };
    };

    // Convert weights to position
    const weightsToPosition = (weights) => {
        const x = triangleLeft + 
            vertices.payload.x * weights.payload +
            vertices.distance.x * weights.distance +
            vertices.speed.x * weights.speed;
            
        const y = triangleTop + 
            vertices.payload.y * weights.payload +
            vertices.distance.y * weights.distance +
            vertices.speed.y * weights.speed;
            
        return { x, y };
    };

    // Calculate initial ball position from weights
    const [ballPosition, setBallPosition] = useState(() => weightsToPosition(weights));
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);

    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const rawX = e.clientX - rect.left;
        const rawY = e.clientY - rect.top;

        // Get barycentric coordinates
        const coords = calculateBarycentricCoordinates(rawX, rawY);

        // Update position and weights
        const newPos = weightsToPosition(coords);
        setBallPosition(newPos);
        setWeights(coords);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Update ball position when weights change
    useEffect(() => {
        if (!isDragging) {
            setBallPosition(weightsToPosition(weights));
        }
    }, [weights, isDragging]);

    useEffect(() => {
        // Add global mouse event listeners
        const handleGlobalMouseMove = (e) => {
            if (isDragging) {
                const rect = document.querySelector('.triangle-container').getBoundingClientRect();
                const rawX = e.clientX - rect.left;
                const rawY = e.clientY - rect.top;

                // Get barycentric coordinates
                const coords = calculateBarycentricCoordinates(rawX, rawY);

                // Update position and weights
                const newPos = weightsToPosition(coords);
                setBallPosition(newPos);
                setWeights(coords);
            }
        };

        const handleGlobalMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleGlobalMouseMove);
            window.addEventListener('mouseup', handleGlobalMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove);
            window.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [isDragging]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ backgroundColor: 'dark_teal.main', color: 'text.main' }}>Adjust Weighting Values</DialogTitle>
            <DialogContent sx={{ backgroundColor: 'dark_teal.main' }}>
                <Box sx={{ backgroundColor: 'navy.main', p: 2, borderRadius: 2 }}>
                    <TriangleContainer
                        className="triangle-container"
                    >
                        <Triangle>
                            <TriangleFill />
                        </Triangle>
                        <DraggableBall
                            position={ballPosition}
                            onMouseDown={handleMouseDown}
                        />
                        <Label sx={{ bottom: -10, left: triangleLeft + triangleWidth/2, transform: 'translateX(-50%)', color: 'text.main' }}>Payload</Label>
                        <Label sx={{ top: -10, left: triangleLeft - 20, color: 'text.main' }}>Distance</Label>
                        <Label sx={{ top: -10, right: triangleLeft - 20, color: 'text.main' }}>Speed</Label>
                    </TriangleContainer>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', minWidth: '100px', backgroundColor: 'navy.main' }}>
                        <Typography variant="h6" sx={{ color: 'text.main' }}>Distance</Typography>
                        <Typography sx={{ color: 'text.main' }}>{(weights.distance * 100).toFixed(1)}%</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, textAlign: 'center', minWidth: '100px', backgroundColor: 'navy.main' }}>
                        <Typography variant="h6" sx={{ color: 'text.main' }}>Speed</Typography>
                        <Typography sx={{ color: 'text.main' }}>{(weights.speed * 100).toFixed(1)}%</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, textAlign: 'center', minWidth: '100px', backgroundColor: 'navy.main' }}>
                        <Typography variant="h6" sx={{ color: 'text.main' }}>Payload</Typography>
                        <Typography sx={{ color: 'text.main' }}>{(weights.payload * 100).toFixed(1)}%</Typography>
                    </Paper>
                </Box>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: 'dark_teal.main' }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={onClose} 
                    variant="contained" 
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default WeightingDialog; 